/**
 * Utility functions for Family Tree visualization with React Flow
 */
import { getShortDisplayName } from "../../Utils/name";
import { getFamilyTreeRootNameParts } from "../../Utils/topOfLineFamilyTitle";

/**
 * Walk API `tree` (root members + nested `children`) and aggregate Gen label counts per `generation_level`.
 * Semantics match sidebar labels: Gen 1 includes spouse on card as a second person (not an in-law);
 * Gen 2+ totals and male/female are primaries only; spouses on the card count toward In-Laws only.
 *
 * @param {Array} treeRoots - Same array passed to `convertToReactFlowData` as `treeData`
 * @returns {Record<number, { totalCount: number, malesCount: number, femalesCount: number, inLawsCount: number }>}
 */
export function aggregateGenerationCountsFromApiTree(treeRoots) {
  const byGen = {};

  const bucket = (gen) => {
    if (!byGen[gen]) {
      byGen[gen] = {
        totalCount: 0,
        malesCount: 0,
        femalesCount: 0,
        inLawsCount: 0,
      };
    }
    return byGen[gen];
  };

  const addPrimaryGender = (b, gender) => {
    if (gender === null || gender === undefined) return;
    if (gender === 1 || Number(gender) === 1) {
      b.malesCount++;
    } else if (gender === 2 || Number(gender) === 2) {
      b.femalesCount++;
    } else if (gender === 3) {
      /* binary — unchanged from node logic */
    } else if (typeof gender === "string") {
      const genderLower = gender.toLowerCase();
      if (genderLower === "male" || genderLower === "m") b.malesCount++;
      else if (genderLower === "female" || genderLower === "f") b.femalesCount++;
    }
  };

  const addSpouseGender = (b, spouseG) => {
    if (spouseG === null || spouseG === undefined) return;
    const sn = Number(spouseG);
    if (sn === 1 || spouseG === 1) {
      b.malesCount++;
    } else if (sn === 2 || spouseG === 2) {
      b.femalesCount++;
    } else if (typeof spouseG === "string") {
      const gl = spouseG.toLowerCase();
      if (gl === "male" || gl === "m") b.malesCount++;
      else if (gl === "female" || gl === "f") b.femalesCount++;
    }
  };

  const hasSpouseOnCard = (m) =>
    Boolean(m.spouse_full_name || m.spouse_first_name);

  const walk = (nodes) => {
    if (!nodes || !nodes.length) return;
    for (const m of nodes) {
      const gen = Number(m.generation_level);
      if (Number.isNaN(gen)) continue;
      const b = bucket(gen);

      if (gen === 1) {
        b.totalCount += 1;
        addPrimaryGender(b, m.gender);
        if (hasSpouseOnCard(m)) {
          b.totalCount += 1;
          addSpouseGender(b, m.spouse_gender);
        }
      } else {
        b.totalCount += 1;
        addPrimaryGender(b, m.gender);
        if (hasSpouseOnCard(m)) {
          b.inLawsCount += 1;
        }
      }

      if (m.children && m.children.length) walk(m.children);
    }
  };

  walk(treeRoots);
  return byGen;
}

/**
 * Convert API family data to React Flow nodes and edges format
 * @param {Array} treeData - Hierarchical tree array from API with children
 * @param {Object} familyTreeInfo - Family tree metadata (color, name, etc.)
 * @returns {Object} { nodes, edges } - React Flow compatible data
 */
export const convertToReactFlowData = (treeData, familyTreeInfo = null , sortOrder = "oldest") => {
  if (!treeData || treeData.length === 0) {
    // console.log('No family members found in tree data');
    return { nodes: [], edges: [] };
  }

  // console.log('Processing hierarchical tree data:', treeData);

  const nodes = [];
  const edges = [];
  const nodeSpacingY = 300; // Vertical spacing between generations (increased for better visibility)
  // Extra vertical gap from Start/Top of the Line (Gen 1) down to first child generation (Gen 2)
  const gen1ToGen2Spacing = 480;
  /** ~3 character spaces between siblings inside the same head-of-line group. */
  const SPACE_WIDTH = 12;
  const siblingGapX = 3 * SPACE_WIDTH;
  /** Separator between head-of-line color groups so each line reads as one cluster. */
  const headOfLineClusterGapX = 4 * SPACE_WIDTH;
  const CHILD_NODE_MIN_WIDTH = 251;
  const CHILD_NODE_MAX_WIDTH = 639;
  const GEN1_NODE_WIDTH = 720;
  const estimateNodeWidth = (member, generation = 1) => {
    if (generation === 1) return GEN1_NODE_WIDTH;
    const hasSpouse = !!(
      member.spouse_first_name ||
      member.spouse_full_name ||
      member.spouseFirstName ||
      member.spouseFullName
    );
    if (!hasSpouse) {
      const nameLen = (
        member.full_name ||
        `${member.first_name || ""} ${member.last_name || ""}`
      ).trim().length;
      return Math.min(
        CHILD_NODE_MAX_WIDTH,
        Math.max(CHILD_NODE_MIN_WIDTH, nameLen * 7 + 48)
      );
    }
    const combinedLen =
      (member.full_name || "").length +
      (
        member.spouse_full_name ||
        `${member.spouse_first_name || ""} ${member.spouse_last_name || ""}`
      ).trim().length;
    return Math.min(
      CHILD_NODE_MAX_WIDTH,
      Math.max(CHILD_NODE_MIN_WIDTH, combinedLen * 7 + 64)
    );
  };
  /** Sidebar label X uses left edge of tree; positions are layout centers when canvas uses nodeOrigin [0.5, 0]. */
  const estimateNodeHalfWidthForLabels = (node) => {
    if ((node.data?.generation ?? 1) === 1) return 360;
    const hasSpouse = !!(node.data?.spouseFirstName || node.data?.spouseFullName);
    return hasSpouse ? 190 : 140;
  };
  const generationLabelWidth = 250;
  /** Gen 1 sidebar: shift both Start/Top banner and Gen 1 stats block left by the same amount so they align. */
  const gen1StartTopLabelXNudge = 42;
  /** Shift Heads of Family Lines/Colors strip right relative to Gen 2 stats below. */
  const gen2HeadsLabelXNudge = 62;
  
  // Default color from family tree info or fallback
  const defaultColor = familyTreeInfo?.tree_hex_code || '#6366f1';

  // Track maximum generation for inversion calculation
  let maxGeneration = 1;
  
  // Track Gen 2 nodes and their deepest descendant nodes for displaying counts
  const gen2NodesMap = new Map(); // Map of Gen 2 member ID -> { count, deepestNodeId, deepestGeneration, colorCode }

  /**
   * Normalize DOB for comparison
   * Handles both timestamps (milliseconds) and date strings (MM/DD/YYYY, MM-DD-YYYY, etc.)
   * @param {*} dob - Date of birth (can be number, string timestamp, or date string)
   * @returns {string|null} - Normalized DOB string for comparison, or null if invalid
   */
  const normalizeDob = (dob) => {
    if (dob === null || dob === undefined || dob === '') return null;
    
    // Handle timestamps (numbers or numeric strings)
    if (typeof dob === 'number') {
      return String(dob);
    }
    
    if (typeof dob === 'string') {
      const trimmed = dob.trim();
      
      // Check if it's a numeric string (timestamp) - handles both positive and negative
      // This matches strings like "1234567890" or "-662362200000"
      if (/^-?\d+$/.test(trimmed)) {
        // For numeric strings, parse and convert back to string to ensure consistency
        // This handles negative timestamps correctly (dates before 1970)
        const parsed = parseInt(trimmed, 10);
        // Check if parsing was successful and didn't lose precision
        if (!isNaN(parsed) && String(parsed) === trimmed) {
          return String(parsed);
        }
        // If parsing changed the value (shouldn't happen for valid timestamps), use original
        return trimmed;
      }
      
      // Try to parse as a date string and normalize to timestamp
      // Handle formats like: MM/DD/YYYY, MM-DD-YYYY, DD/MM/YYYY, etc.
      try {
        // Try parsing the date string
        const date = new Date(trimmed);
        
        // Check if it's a valid date
        if (!isNaN(date.getTime())) {
          // Normalize to timestamp string for consistent comparison
          return String(date.getTime());
        }
      } catch (e) {
        // If parsing fails, fall back to lowercase string comparison
        // This handles edge cases where date format is unexpected
      }
      
      // Fallback: return lowercase for string comparison
      return trimmed.toLowerCase();
    }
    
    // Fallback for other types
    return String(dob).trim().toLowerCase();
  };

  /**
   * Convert DOB to MM-DD-YYYY format for comparison
   * @param {*} dob - Date of birth (can be number, string timestamp, or date string)
   * @returns {string|null} - DOB in MM-DD-YYYY format, or null if invalid
   */
  const dobToMMDDYYYY = (dob) => {
    if (dob === null || dob === undefined || dob === '') return null;
    
    let date;
    
    // Handle timestamps (numbers or numeric strings)
    if (typeof dob === 'number') {
      date = new Date(dob);
    } else if (typeof dob === 'string') {
      const trimmed = dob.trim();
      
      // Check if it's a numeric string (timestamp)
      if (/^-?\d+$/.test(trimmed)) {
        const parsed = parseInt(trimmed, 10);
        if (!isNaN(parsed)) {
          date = new Date(parsed);
        } else {
          return null;
        }
      } else {
        // Try to parse as a date string
        date = new Date(trimmed);
      }
    } else {
      return null;
    }
    
    // Check if date is valid
    if (!date || isNaN(date.getTime())) {
      return null;
    }
    
    // Convert to MM-DD-YYYY format using UTC calendar components
    const month = String(date.getUTCMonth() + 1).padStart(2, '0'); // Months are 0-indexed
    const day = String(date.getUTCDate()).padStart(2, '0');
    const year = date.getUTCFullYear();
    
    return `${month}-${day}-${year}`;
  };

  /**
   * Get DOB as timestamp at start of day (local) for ±N day comparison
   * @param {*} dob - Date of birth (same formats as dobToMMDDYYYY)
   * @returns {number|null} - ms at midnight that day, or null
   */
  const getDobDayTimestamp = (dob) => {
    if (dob === null || dob === undefined || dob === '') return null;
    let date;
    if (typeof dob === 'number') {
      date = new Date(dob);
    } else if (typeof dob === 'string') {
      const trimmed = dob.trim();
      if (/^-?\d+$/.test(trimmed)) {
        const parsed = parseInt(trimmed, 10);
        date = isNaN(parsed) ? null : new Date(parsed);
      } else {
        date = new Date(trimmed);
      }
    } else {
      return null;
    }
    if (!date || isNaN(date.getTime())) return null;
    return Date.UTC(date.getUTCFullYear(), date.getUTCMonth(), date.getUTCDate());
  };

  /**
   * Check if two DOBs are within ±rangeDays (for twin/triplet: same birth = within 3 days)
   * @param {*} dob1 - First DOB
   * @param {*} dob2 - Second DOB
   * @param {number} rangeDays - Default 3
   * @returns {boolean}
   */
  const isDobWithinRange = (dob1, dob2, rangeDays = 3) => {
    const t1 = getDobDayTimestamp(dob1);
    const t2 = getDobDayTimestamp(dob2);
    if (t1 == null || t2 == null) return false;
    const msPerDay = 24 * 60 * 60 * 1000;
    return Math.abs(t1 - t2) <= rangeDays * msPerDay;
  };

  /**
   * Check if all DOBs in the array are within ±rangeDays of each other
   * @param {Array} dobs - Array of DOB values
   * @param {number} rangeDays - Default 3
   * @returns {boolean}
   */
  const areDobsWithinRange = (dobs, rangeDays = 3) => {
    const timestamps = dobs.map(d => getDobDayTimestamp(d)).filter(t => t != null);
    if (timestamps.length === 0) return true;
    if (timestamps.length === 1) return true;
    const minTs = Math.min(...timestamps);
    const maxTs = Math.max(...timestamps);
    const msPerDay = 24 * 60 * 60 * 1000;
    return (maxTs - minTs) <= rangeDays * msPerDay;
  };

  /**
   * Horizontal space (px) for a subtree: node width + gap, or children's row if wider.
   */
  const calculateSubtreeWidth = (member, generation = 1) => {
    const nodeW = estimateNodeWidth(member, generation);
    if (!member.children || member.children.length === 0) {
      return nodeW + siblingGapX;
    }
    const childrenTotal = member.children.reduce(
      (sum, child) => sum + calculateSubtreeWidth(child, generation + 1),
      0
    );
    return Math.max(nodeW + siblingGapX, childrenTotal);
  };

  /**
   * Count all descendants (main members only, excluding spouses) for a given member
   * @param {Object} member - The member to count descendants for
   * @returns {Number} Total count of descendants (main members only)
   */
  const countDescendants = (member) => {
    let count = 0;
    
    // Count all children (main members only, not spouses)
    if (member.children && member.children.length > 0) {
      member.children.forEach(child => {
        count++; // Count the child (main member)
        // Recursively count descendants
        count += countDescendants(child);
      });
    }
    
    return count;
  };

  /**
   * Count all descendants with detailed breakdown (main members, gender, and spouses)
   * @param {Object} member - The member to count descendants for
   * @returns {Object} { mainCount, malesCount, femalesCount, spousesCount }
   */
  const countDescendantsDetailed = (member) => {
    let mainCount = 0;
    let malesCount = 0;
    let femalesCount = 0;
    let spousesCount = 0;
    
    // Count all children (main members only, not spouses)
    if (member.children && member.children.length > 0) {
      member.children.forEach(child => {
        // Count the child (main member)
        mainCount++;
        
        // Count gender for main member
        const gender = child.gender;
        if (gender !== null && gender !== undefined) {
          if (gender === 1) {
            malesCount++;
          } else if (gender === 2) {
            femalesCount++;
          }
        } else if (typeof gender === 'string') {
          const genderLower = gender.toLowerCase();
          if (genderLower === 'male' || genderLower === 'm') {
            malesCount++;
          } else if (genderLower === 'female' || genderLower === 'f') {
            femalesCount++;
          }
        }
        
        // Count spouse if exists
        if (child.spouse_first_name || child.spouse_full_name) {
          spousesCount++;
        }
        
        // Recursively count descendants
        const childDetails = countDescendantsDetailed(child);
        mainCount += childDetails.mainCount;
        malesCount += childDetails.malesCount;
        femalesCount += childDetails.femalesCount;
        spousesCount += childDetails.spousesCount;
      });
    }
    
    return { mainCount, malesCount, femalesCount, spousesCount };
  };

  /**
   * Parse multiple_birth_details JSON string or array to extract family member IDs
   * @param {*} multipleBirthDetails - Can be JSON string, array, or null
   * @returns {Array} Array of family member IDs
   */
  const parseMultipleBirthDetails = (multipleBirthDetails) => {
    if (!multipleBirthDetails) return [];
    
    try {
      // If it's already an array, use it directly
      if (Array.isArray(multipleBirthDetails)) {
        return multipleBirthDetails
          .map(detail => {
            // Extract family_member_id (handle both possible field names)
            const memberId = detail.family_member_id || detail.famiy_member_id;
            return memberId ? String(memberId) : null;
          })
          .filter(Boolean);
      }
      
      // If it's a string, try to parse it as JSON
      if (typeof multipleBirthDetails === 'string') {
        const parsed = JSON.parse(multipleBirthDetails);
        if (Array.isArray(parsed)) {
          return parsed
            .map(detail => {
              // Extract family_member_id (handle both possible field names)
              const memberId = detail.family_member_id || detail.famiy_member_id;
              return memberId ? String(memberId) : null;
            })
            .filter(Boolean);
        }
      }
    } catch (e) {
      console.warn('Error parsing multiple_birth_details:', e);
    }
    
    return [];
  };

  /**
   * Group siblings by multiple_birth_details to identify all twin/triplet sets and assign set numbers
   * NEW LOGIC: Only show tags if multiple_birth_details is not null
   * Group members whose IDs are referenced in multiple_birth_details together
   * Then verify they have the same DOB (if DOB exists)
   * @param {Array} siblings - Array of all siblings at the current generation level
   * @returns {Map} Map of member ID -> { setNumber, type, totalInSet, birthOrder }
   *                setNumber: 1 for first set, 2 for second set, etc. (per generation)
   */
  const identifyTwinsTripletsSets = (siblings) => {
    const memberSetMap = new Map();
    if (!siblings || siblings.length < 2) {
      return memberSetMap;
    }

    // Step 1: Find all members with multiple_birth_details and build a graph of connections
    const membersWithMultipleBirthDetails = new Map(); // Map of member ID -> member object
    const idToMemberMap = new Map(); // Map of member ID string -> member object
    const memberDetailsMap = new Map(); // Map of member ID -> { details, order }
    const connectionGraph = new Map(); // Map of member ID -> Set of connected member IDs
    
    // Build maps and find all members with multiple_birth_details
    siblings.forEach(member => {
      const memberIdStr = String(member.id);
      idToMemberMap.set(memberIdStr, member);
      
      const multipleBirthDetails = member.multiple_birth_details;
      
      // Only process if multiple_birth_details is not null
      if (multipleBirthDetails !== null && multipleBirthDetails !== undefined && multipleBirthDetails !== '') {
        membersWithMultipleBirthDetails.set(memberIdStr, member);
        
        // Parse the multiple_birth_details to get all referenced IDs
        const parsedIds = parseMultipleBirthDetails(multipleBirthDetails);
        
        // Initialize connection set for this member
        if (!connectionGraph.has(memberIdStr)) {
          connectionGraph.set(memberIdStr, new Set());
        }
        
        // Add all referenced IDs to the connection graph
        parsedIds.forEach(id => {
          connectionGraph.get(memberIdStr).add(id);
          // Also create reverse connection
          if (!connectionGraph.has(id)) {
            connectionGraph.set(id, new Set());
          }
          connectionGraph.get(id).add(memberIdStr);
        });
        
        // Also add the current member to its own connections (they're part of the set)
        connectionGraph.get(memberIdStr).add(memberIdStr);
        
        // Store the details and order for this member
        let order = member.multiple_birth_order_number;
        if (order === null || order === undefined || order === '') {
          // Try to get order from multiple_birth_details array
          try {
            let detailsArray = multipleBirthDetails;
            if (typeof multipleBirthDetails === 'string') {
              detailsArray = JSON.parse(multipleBirthDetails);
            }
            if (Array.isArray(detailsArray)) {
              // Find the detail entry for this member (where family_member_id matches member.id or is null)
              const memberDetail = detailsArray.find(d => {
                // Handle both possible field name spellings
                const detailId = String((d.family_member_id || d.famiy_member_id || ''));
                const memberIdStr = String(member.id);
                // If family_member_id is null or empty, this might be the current member
                return detailId === memberIdStr || (detailId === '' && (d.order || d.multiple_birth_order));
              });
              if (memberDetail) {
                // Check for 'order' field first (new format), then fallback to 'multiple_birth_order' (old format)
                order = memberDetail.order || memberDetail.multiple_birth_order || order;
              }
            }
          } catch (e) {
            // Ignore parsing errors
          }
        }
        
        memberDetailsMap.set(member.id, {
          details: multipleBirthDetails,
          order: order
        });
      }
    });
    
    // Step 2: Find connected components (groups of members that reference each other)
    const processedMembers = new Set();
    let setNumber = 0;
    
    // DFS to find all connected members in a set
    const findConnectedSet = (startId, visited) => {
      const connected = new Set();
      const stack = [startId];
      
      while (stack.length > 0) {
        const currentId = stack.pop();
        if (visited.has(currentId)) continue;
        
        visited.add(currentId);
        connected.add(currentId);
        
        // Get all connected members
        const connections = connectionGraph.get(currentId);
        if (connections) {
          connections.forEach(connectedId => {
            if (!visited.has(connectedId) && idToMemberMap.has(connectedId)) {
              stack.push(connectedId);
            }
          });
        }
      }
      
      return connected;
    };
    
    // Process each member with multiple_birth_details
    membersWithMultipleBirthDetails.forEach((member, memberIdStr) => {
      if (processedMembers.has(memberIdStr)) return;
      
      // Find all connected members (the set)
      const connectedSet = findConnectedSet(memberIdStr, new Set());
      
      // Convert to member objects and filter to only include actual siblings
      const membersInSet = Array.from(connectedSet)
        .map(id => idToMemberMap.get(id))
        .filter(m => m !== undefined && siblings.some(s => String(s.id) === String(m.id)));
      
      // Verify all members have DOB within ±3 days (exact or near match for twin/triplet badge)
      if (membersInSet.length >= 2) {
        const dobs = membersInSet.map(m => m.dob).filter(d => d != null && d !== '');
        if (dobs.length > 0 && !areDobsWithinRange(dobs, 3)) {
          return; // DOBs not all within ±3 days, skip this set
        }
        
        // Valid set found
        setNumber++;
        
        // Determine type based on count of members in the set
        let type = 'twins'; // Default for 2 people
        const count = membersInSet.length;
        if (count === 3) {
          type = 'triplets';
        } else if (count === 4) {
          type = 'quads';
        } else if (count === 5) {
          type = 'quins';
        } else if (count === 6) {
          type = 'sexs';
        } else if (count === 7) {
          type = 'seps';
        } else if (count === 8) {
          type = 'ocs';
        } else if (count === 9) {
          type = 'nuns';
        }

        // Sort members within the set to determine birth order
        // Priority: 1. multiple_birth_order_number, 2. order from multiple_birth_details, 3. array index
        const sortedMembers = [...membersInSet].sort((a, b) => {
          // First, try to use multiple_birth_order_number if available
          const orderA = a.multiple_birth_order_number;
          const orderB = b.multiple_birth_order_number;
          
          if (orderA !== null && orderA !== undefined && orderA !== '' && 
              orderB !== null && orderB !== undefined && orderB !== '') {
            const numA = typeof orderA === 'string' ? parseInt(orderA, 10) : orderA;
            const numB = typeof orderB === 'string' ? parseInt(orderB, 10) : orderB;
            if (!isNaN(numA) && !isNaN(numB)) {
              return numA - numB;
            }
          }
          
          // Second, try to use order from multiple_birth_details
          const detailsA = memberDetailsMap.get(a.id);
          const detailsB = memberDetailsMap.get(b.id);
          if (detailsA && detailsA.order && detailsB && detailsB.order) {
            const numA = typeof detailsA.order === 'string' ? parseInt(detailsA.order, 10) : detailsA.order;
            const numB = typeof detailsB.order === 'string' ? parseInt(detailsB.order, 10) : detailsB.order;
            if (!isNaN(numA) && !isNaN(numB)) {
              return numA - numB;
            }
          }
          
          // Fallback: use original position in siblings array
          const indexA = siblings.findIndex(s => String(s.id) === String(a.id));
          const indexB = siblings.findIndex(s => String(s.id) === String(b.id));
          return indexA - indexB;
        });

        // Assign set number and birth order to all members in this set
        sortedMembers.forEach((member, index) => {
          const memberIdStr = String(member.id);
          processedMembers.add(memberIdStr);
          memberSetMap.set(member.id, {
            setNumber: setNumber,
            type: type,
            totalInSet: count,
            birthOrder: index + 1 // 1, 2, 3, etc. (will be displayed as "Twin 1", "Twin 2", etc.)
          });
        });
      }
    });

    return memberSetMap;
  };

  /**
   * Check if a member is part of a twin/triplet set based on their siblings
   * @param {Object} member - The member to check
   * @param {Array} siblings - Array of all siblings (including the member)
   * @param {Map} setMap - Map of member ID -> set info (from identifyTwinsTripletsSets)
   * @returns {Object} { isPartOfSet, setNumber, type, birthOrder } - Whether member is part of set and details
   */
  const checkIfMemberIsPartOfTwinsTriplets = (member, siblings, setMap) => {
    if (!setMap || setMap.size === 0) {
      return { isPartOfSet: false, setNumber: 0, type: null, birthOrder: 0 };
    }

    const setInfo = setMap.get(member.id);
    if (setInfo) {
      return {
        isPartOfSet: true,
        setNumber: setInfo.setNumber,
        type: setInfo.type,
        birthOrder: setInfo.birthOrder || 0
      };
    }

    return { isPartOfSet: false, setNumber: 0, type: null, birthOrder: 0 };
  };

  /**
   * Calculate twins/triplets sets from children
   * Detects twins/triplets by:
   * 1. Checking the 'twin' field on children
   * 2. Comparing DOBs of siblings (same DOB = twins/triplets)
   * @param {Object} member - The member to check children for
   * @returns {Object} { setsCount, type } - Number of sets and type (twins/triplets)
   */
  const calculateTwinsTripletsSets = (member) => {
    if (!member.children || member.children.length === 0) {
      return { setsCount: 0, type: null };
    }

    let twinsSets = 0;
    let tripletsSets = 0;

    // Method 1: Check twin field on children
    const twinsByField = member.children.filter(child => {
      const twinValue = child.twin;
      return twinValue === 2 || twinValue === "2" || twinValue === "Twins" || twinValue === "twins";
    });
    const tripletsByField = member.children.filter(child => {
      const twinValue = child.twin;
      return twinValue === 3 || twinValue === "3" || twinValue === "Triplets" || twinValue === "triplets";
    });

    twinsSets += Math.floor(twinsByField.length / 2);
    tripletsSets += Math.floor(tripletsByField.length / 3);

    // Method 2: Group children by DOB within ±3 days to detect twins/triplets (exact or near match)
    const childrenWithDob = member.children.filter(c => getDobDayTimestamp(c.dob) != null);
    const dobClusters = []; // each cluster = array of children whose DOBs are within ±3 days (transitively)
    childrenWithDob.forEach(child => {
      const matchingClusters = dobClusters.filter(cluster =>
        cluster.some(m => isDobWithinRange(m.dob, child.dob, 3))
      );
      if (matchingClusters.length === 0) {
        dobClusters.push([child]);
      } else if (matchingClusters.length === 1) {
        matchingClusters[0].push(child);
      } else {
        const merged = matchingClusters.flat();
        merged.push(child);
        const indicesToRemove = matchingClusters.map(c => dobClusters.indexOf(c)).sort((a, b) => b - a);
        indicesToRemove.forEach(i => dobClusters.splice(i, 1));
        dobClusters.push(merged);
      }
    });

    dobClusters.forEach((childrenWithSameDob) => {
      const count = childrenWithSameDob.length;
      if (count < 2) return;
      const alreadyCounted = childrenWithSameDob.some(child => {
        const twinValue = child.twin;
        return twinValue === 2 || twinValue === "2" || twinValue === 3 || twinValue === "3" ||
               twinValue === "Twins" || twinValue === "twins" ||
               twinValue === "Triplets" || twinValue === "triplets";
      });
      if (!alreadyCounted) {
        if (count === 2) {
          twinsSets += 1;
        } else if (count >= 3) {
          tripletsSets += Math.floor(count / 3);
          const remainder = count % 3;
          if (remainder === 2) {
            twinsSets += 1;
          }
        }
      }
    });

    // Return the result - prioritize triplets if both exist
    if (tripletsSets > 0 && tripletsSets >= twinsSets) {
      return { setsCount: tripletsSets, type: 'triplets' };
    } else if (twinsSets > 0) {
      return { setsCount: twinsSets, type: 'twins' };
    }

    return { setsCount: 0, type: null };
  };
  

  /**
   * Lay out each Gen 2 head-of-line as its own compact cluster, then place clusters
   * side-by-side. Avoids sharing one centered row (which spreads wide branches apart).
   */
  const layoutGen2HeadOfLineBranches = (gen1Member, gen1CenterX) => {
    const gen2Children = gen1Member.children || [];
    if (gen2Children.length === 0) return;

    const branchWidths = gen2Children.map((child) =>
      calculateSubtreeWidth(child, 2)
    );
    const clusterGaps =
      Math.max(0, gen2Children.length - 1) * headOfLineClusterGapX;
    const totalWidth =
      branchWidths.reduce((sum, width) => sum + width, 0) + clusterGaps;

    let branchLeft = gen1CenterX - totalWidth / 2;

    // Gen 2 branches are laid out one at a time, so each processTreeLevel call below only sees a
    // single member. Multiple-birth sets have to be identified across the whole Gen 2 sibling row
    // first, otherwise twins who each head their own family line/color never get their badge.
    const gen2TwinsTripletsSetMap = identifyTwinsTripletsSets(gen2Children);

    gen2Children.forEach((child, index) => {
      const branchWidth = branchWidths[index];
      const branchCenterX = branchLeft + branchWidth / 2;
      processTreeLevel(
        [child],
        2,
        gen1Member.id,
        branchCenterX,
        branchWidth,
        null,
        null,
        gen2TwinsTripletsSetMap,
        gen2Children
      );
      branchLeft += branchWidth;
      if (index < gen2Children.length - 1) {
        branchLeft += headOfLineClusterGapX;
      }
    });
  };

  /**
   * Pack each head-of-line cluster edge-to-edge (with cluster gaps) so groups stay
   * cohesive and the overall tree uses horizontal space efficiently.
   */
  const packHeadOfLineClusters = () => {
    const groups = new Map();
    nodes.forEach((node) => {
      if (node.type !== "familyMember" || !node.data?.headOfLineId) return;
      const key = String(node.data.headOfLineId);
      if (!groups.has(key)) groups.set(key, []);
      groups.get(key).push(node);
    });
    if (groups.size <= 1) return;

    const orderedGroups = [...groups.values()].sort((groupA, groupB) => {
      const minA = Math.min(...groupA.map((n) => n.position.x));
      const minB = Math.min(...groupB.map((n) => n.position.x));
      return minA - minB;
    });

    let clusterCursor = null;
    orderedGroups.forEach((groupNodes) => {
      const leftEdge = Math.min(
        ...groupNodes.map(
          (n) => n.position.x - estimateNodeHalfWidthForLabels(n)
        )
      );
      const rightEdge = Math.max(
        ...groupNodes.map(
          (n) => n.position.x + estimateNodeHalfWidthForLabels(n)
        )
      );

      if (clusterCursor === null) {
        clusterCursor = leftEdge;
      }
      const dx = clusterCursor - leftEdge;
      groupNodes.forEach((node) => {
        node.position.x += dx;
      });
      clusterCursor += rightEdge - leftEdge + headOfLineClusterGapX;
    });

    const gen1Nodes = nodes.filter((n) => n.data?.generation === 1);
    if (gen1Nodes.length === 1) {
      const memberNodes = nodes.filter((n) => n.type === "familyMember");
      const treeLeft = Math.min(
        ...memberNodes.map(
          (n) => n.position.x - estimateNodeHalfWidthForLabels(n)
        )
      );
      const treeRight = Math.max(
        ...memberNodes.map(
          (n) => n.position.x + estimateNodeHalfWidthForLabels(n)
        )
      );
      gen1Nodes[0].position.x = (treeLeft + treeRight) / 2;
    }
  };

  /**
   * Recursively process tree nodes and their children
   * @param {Array} members - Array of member objects
   * @param {Number} generation - Current generation level (1 = root)
   * @param {String} parentId - Parent member ID for creating edges
   * @param {Number} startX - Starting X position for this branch
   * @param {Number} branchWidth - Width allocated for this branch
   * @param {String} headOfLineId - ID of the Gen 2 head of line this branch belongs to
   * @param {String|null} headOfLineShortName - Short label for that head (badge on all descendants in the line)
   */
  const processTreeLevel = (members, generation, parentId = null, startX = 0, branchWidth = 0, headOfLineId = null, headOfLineShortName = null, precomputedTwinsTripletsSetMap = null, twinsTripletsSiblings = null) => {
    if (!members || members.length === 0) return;

    const memberCount = members.length;


    // Update max generation
    maxGeneration = Math.max(maxGeneration, generation);
    
    // Calculate total width needed for all siblings (pixel-based slots)
    const subtreeWidths = members.map((m) => calculateSubtreeWidth(m, generation));
    const clusterGaps =
      generation === 2
        ? Math.max(0, members.length - 1) * headOfLineClusterGapX
        : 0;
    const totalWidth =
      subtreeWidths.reduce((sum, width) => sum + width, 0) + clusterGaps;

    // Re-centering shift only at the top level (Gen 1 → Gen 2) where there are no cousin subtrees
    // to overlap. Applying it deeper would push wider subtrees past their slot and into siblings' space.
    let centeringShift = 0;
    if (generation === 2) {
      const firstHalf = (subtreeWidths[0] || 0) / 2;
      const lastHalf = (subtreeWidths[subtreeWidths.length - 1] || 0) / 2;
      centeringShift = (firstHalf - lastHalf) / 2;
    }

    let currentX = startX - totalWidth / 2 - centeringShift;
    // console.log("member info in convert to react flow : ",members[0])

    // First, identify all twin/triplet sets among siblings at this generation level and assign set numbers
    // This works for all generations - each generation processes its siblings independently
    // Set numbers (1, 2, 3, etc.) are assigned per generation
    // Use the caller's set map when siblings are processed one branch at a time (Gen 2).
    const twinsTripletsSiblingList = twinsTripletsSiblings || members;
    const twinsTripletsSetMap =
      precomputedTwinsTripletsSetMap || identifyTwinsTripletsSets(members);

    members.forEach((member, index) => {
      const memberId = `member-${member.id}`;
      const memberWidth = subtreeWidths[index];
      
      // Center this member in its allocated space
      // Calculate Y position (will be inverted later if needed)
      // Use larger vertical gap from Start/Top of the Line (Gen 1) to first child generation (Gen 2)
      const yPosition = generation === 1
        ? 0
        : gen1ToGen2Spacing + (generation - 2) * nodeSpacingY;
      // Horizontal center of this member's subtree band. Canvas uses nodeOrigin [0.5, 0] so position.x is this center.
      const layoutCenterX = currentX + memberWidth / 2;

      // Determine head of line ID: if this is Gen 2, it's the head; otherwise inherit from parent
      const currentHeadOfLineId = generation === 2 ? member.id : headOfLineId;
      const currentHeadShortName =
        generation === 2
          ? getShortDisplayName(member.first_name, member.suffix, member.special_name)
          : headOfLineShortName;
      const memberColorCode = member.hex_code || member.color_code || defaultColor;

      // Extract photo URL
      let photoUrl = member.image || null;
    
      // Check if this member is part of a twin/triplet set with their siblings
      const memberTwinsTripletsInfo = checkIfMemberIsPartOfTwinsTriplets(member, twinsTripletsSiblingList, twinsTripletsSetMap);
    
      // Calculate twins/triplets sets for this member's children (for parent nodes)
      const childrenTwinsTripletsInfo = calculateTwinsTripletsSets(member);
    
// console.log("member info in familyTreeUtils : " , member)
      // Create node
      const node = {
        id: memberId,
        type: 'familyMember',
        position: {
          x: layoutCenterX,
          y: yPosition
        },

        data: {
          // Basic member information
          id: member.id,
          user_id: member.user_id,
          spouse_user_id: member.spouse_user_id,
          related_member_id: member.related_member_id,
          generation_level: member.generation_level,

          // Name fields
          firstName: member.first_name,
          middleName: member.middle_name,
          lastName: member.last_name,
          suffix: member.suffix,
          maidenName: member.maiden_name,
          nickName: member.nick_name,
          fullName: member.full_name,
          special_name: member.special_name,

          // Personal details
          gender: member.gender,
          dob: member.dob,
          dateOfDeath: member.date_of_death,
          ageOfDeath: member.age_at_death,
          aliveStatus: member.present_or_deceased,
          // aliveStatus: member.present_or_deceased,

          // Location and status
          stateOfBirth: member.state_of_birth,
          currentStateResiding: member.current_state_residing,
          maritalStatus: member.marital_status,
          weddingAnniversary: member.wedding_anniversary,
          twin: member.twin,
          notes: member.notes,
          address: member.address,

          // Contact information
          email: member.email || member.primary_email ||'',
          phone: member.phone || member.primary_phone_number || '',
          countryCode: member.country_code || '',
          // cell_type: member.primary_cell_type ,
          cell_type: member.primary_cell_type || member.phone_type || null,
          
          // Spouse information
          spouseFirstName: member.spouse_first_name,
          spouseMiddleName: member.spouse_middle_name,
          spouseLastName: member.spouse_last_name,
          spouseSuffix: member.spouse_suffix,
          spouseSpecialName: member.spouse_special_name,
          spouseMaidenName: member.spouse_maiden_name,
          spouseNickName: member.spouse_nick_name,
          spouseFullName: member.spouse_full_name,
          spouseGender: member.spouse_gender,
          spouseDob: member.spouse_dob,
          spouseDateOfDeath: member.spouse_date_of_death,
          spouseAgeAtDeath: member.spouse_age_at_death,
          spouseAliveStatus: member.spouse_present_or_deceased,
          spouseRelation: member.spouse_relation,
          spouseStateOfBirth: member.spouse_state_of_birth,
          spouseCurrentStateResiding: member.spouse_current_state_residing,
          spouseImage: member.spouse_image,
          spouseEmail: member.spouse_email || member.spouse_email_from_user || '',
          spousePhone: member.spouse_phone || member.spouse_phone_number_from_user || '',
          spouseCellType: member.spouse_phone_type || member.spouse_cell_type || '',
          spouseCountryCode: member.spouse_country_code || '',

          // Display and styling
          relation: member.relation,
          photo: photoUrl,
          colorCode: member.hex_code || member.color_code || defaultColor,
          colorName: member.color_name,
          colorInherited: member.color_inherited,
          isRootHead: generation === 1,
          generation: generation,
          /** Immediate parent member id — used to uniquely count multiple-birth sets in the legend (set # resets per sibling group). */
          parentMemberId: parentId != null && parentId !== undefined ? parentId : null,

          // Twins/Triplets information
          // Show indicator if this member is part of a twin/triplet set (not just if they have twins as children)
          // setNumber represents which set they belong to (1st set = 1, 2nd set = 2, etc.)
          // birthOrder represents their order within the set (1st, 2nd, 3rd, 4th, etc.)
          twinsTripletsSetsCount: memberTwinsTripletsInfo.isPartOfSet ? memberTwinsTripletsInfo.setNumber : 0,
          twinsTripletsType: memberTwinsTripletsInfo.isPartOfSet ? memberTwinsTripletsInfo.type : null,
          twinsTripletsBirthOrder: memberTwinsTripletsInfo.isPartOfSet ? memberTwinsTripletsInfo.birthOrder : 0,

          //parent info
          parent_name: member.parent_name,
          parent_special_name: member.parent_special_name,
          parent_color_name : member.parent_color_name,
          parent_hex_code : member.parent_hex_code,
          adopted_parent: member.adopted_parent,
          step_parent: member.step_parent,
          bio_descendant_parent: member.bio_descendant_parent,
          bio_non_descendant_parent: member.bio_non_descendant_parent,
          relation_to_head_of_line: member.relation_to_head_of_line,
          
          // Track head of line ID for finding deepest node
          headOfLineId: currentHeadOfLineId,
          /** Top badge: Gen 2 head's short name for this color line (same for all descendants). */
          headOfLineBadgeName:
            generation >= 2 && currentHeadShortName
              ? currentHeadShortName
              : null,
        }
      };
      
      nodes.push(node);
      
      // For Gen 2 nodes, calculate and store total count (Gen 2 node itself + all descendants)
      if (generation === 2) {
        const descendantCount = countDescendants(member);
        const descendantDetails = countDescendantsDetailed(member);
        
        // Count includes the Gen 2 node itself (1) + all descendants
        const totalCount = 1 + descendantCount;
        
        // Add Gen 2 node's own gender to counts
        let gen2MalesCount = descendantDetails.malesCount;
        let gen2FemalesCount = descendantDetails.femalesCount;
        const gen2Gender = member.gender;
        if (gen2Gender !== null && gen2Gender !== undefined) {
          if (gen2Gender === 1) {
            gen2MalesCount++;
          } else if (gen2Gender === 2) {
            gen2FemalesCount++;
          }
        } else if (typeof gen2Gender === 'string') {
          const genderLower = gen2Gender.toLowerCase();
          if (genderLower === 'male' || genderLower === 'm') {
            gen2MalesCount++;
          } else if (genderLower === 'female' || genderLower === 'f') {
            gen2FemalesCount++;
          }
        }
        
        // Count Gen 2 node's spouse if exists
        let gen2SpousesCount = descendantDetails.spousesCount;
        if (member.spouse_first_name || member.spouse_full_name) {
          gen2SpousesCount++;
        }
        
        gen2NodesMap.set(member.id, {
          count: totalCount,
          mainCount: totalCount, // Main member count (includes Gen 2 node)
          malesCount: gen2MalesCount,
          femalesCount: gen2FemalesCount,
          spousesCount: gen2SpousesCount,
          deepestNodeId: null, // Will be set later
          deepestGeneration: generation,
          colorCode: memberColorCode
        });
      } else if (currentHeadOfLineId) {
        // Track the deepest node for this head of line
        const headOfLineInfo = gen2NodesMap.get(currentHeadOfLineId);
        if (headOfLineInfo) {
          if (generation > headOfLineInfo.deepestGeneration) {
            headOfLineInfo.deepestGeneration = generation;
            headOfLineInfo.deepestNodeId = memberId;
          }
        }
      }


      // Create edge from parent to this node
      if (parentId) {
        
        edges.push({
          id: `edge-${parentId}-${member.id}`,
          source: `member-${parentId}`,
          target: memberId,
          
          type: 'step', // Changed to 'step' for straight lines like Figma design
          animated: false,
          style: {
            stroke: member.hex_code || member.color_code || defaultColor,
            strokeWidth: 3 // Increased to 3px for better visibility
          }
        });
      }

      // Recursively process children
      if (member.children && member.children.length > 0) {
        if (generation === 1) {
          layoutGen2HeadOfLineBranches(member, layoutCenterX);
        } else {
          processTreeLevel(
            member.children,
            generation + 1,
            member.id,
            layoutCenterX,
            memberWidth,
            currentHeadOfLineId,
            currentHeadShortName
          );
        }
      }
      
      // Move currentX to the right for the next sibling
      currentX += memberWidth;
      if (generation === 2 && index < members.length - 1) {
        currentX += headOfLineClusterGapX;
      }
    });
  };

  // Start processing from root level (generation 1)
  processTreeLevel(treeData, 1, null, 0, 0, null);

  // Center each parent on the horizontal midpoint of its direct children's centers.
  // Initial layout places the parent at the center of its subtree *band* among siblings; that
  // can differ from the midpoint of the child row when subtree widths differ (centeringShift
  // only ran for generation === 2). Moving only the parent keeps cousin spacing unchanged.
  const nodeById = new Map(nodes.map((n) => [n.id, n]));
  const childIdsBySource = new Map();
  edges.forEach((e) => {
    if (!e.source?.startsWith("member-") || !e.target?.startsWith("member-")) return;
    const list = childIdsBySource.get(e.source);
    if (list) list.push(e.target);
    else childIdsBySource.set(e.source, [e.target]);
  });
  const memberNodesForCenter = nodes.filter(
    (n) => n.type === "familyMember" && n.data?.generation != null
  );
  const maxGenForCenter = Math.max(0, ...memberNodesForCenter.map((n) => n.data.generation || 0));
  for (let g = maxGenForCenter; g >= 1; g--) {
    memberNodesForCenter
      .filter((n) => (n.data.generation || 0) === g)
      .forEach((parentNode) => {
        const childIds = childIdsBySource.get(parentNode.id);
        if (!childIds || childIds.length === 0) return;
        let sum = 0;
        let count = 0;
        for (const cid of childIds) {
          const c = nodeById.get(cid);
          if (c && typeof c.position.x === "number") {
            sum += c.position.x;
            count++;
          }
        }
        if (count === 0) return;
        parentNode.position.x = sum / count;
      });
  }

  packHeadOfLineClusters();

  // After processing all nodes, add descendant count to the deepest node in each color line
  gen2NodesMap.forEach((info, gen2MemberId) => {
    if (info.deepestNodeId) {
      // Add count to the deepest descendant node
      const deepestNode = nodes.find(n => n.id === info.deepestNodeId);
      if (deepestNode) {
        deepestNode.data.descendantCount = info.count;
        deepestNode.data.descendantMainCount = info.mainCount;
        deepestNode.data.descendantMalesCount = info.malesCount;
        deepestNode.data.descendantFemalesCount = info.femalesCount;
        deepestNode.data.descendantSpousesCount = info.spousesCount;
        deepestNode.data.isDeepestInLine = true;
      }
    } else {
      // If no descendants, the Gen 2 node itself is the deepest and should show count of 1
      const gen2Node = nodes.find(n => n.data.id === gen2MemberId && n.data.generation === 2);
      if (gen2Node) {
        gen2Node.data.descendantCount = info.count; // This will be 1 (just the Gen 2 node itself)
        gen2Node.data.descendantMainCount = info.mainCount;
        gen2Node.data.descendantMalesCount = info.malesCount;
        gen2Node.data.descendantFemalesCount = info.femalesCount;
        gen2Node.data.descendantSpousesCount = info.spousesCount;
        gen2Node.data.isDeepestInLine = true;
      }
    }
  });

  // Group nodes by generation
  const nodesByGeneration = {};
  nodes.forEach(node => {
    if (node.type === 'familyMember') {
      const gen = node.data?.generation || node.data?.generation_level;
      if (gen) {
        if (!nodesByGeneration[gen]) {
          nodesByGeneration[gen] = [];
        }
        nodesByGeneration[gen].push(node);
      }
    }
  });

  // Find the absolute leftmost X position across ALL generations for vertical alignment
  let globalLeftmostX = Infinity;
  Object.values(nodesByGeneration).forEach(genNodes => {
    if (genNodes.length > 0) {
      const genLeftmostX = Math.min(
        ...genNodes.map((node) => node.position.x - estimateNodeHalfWidthForLabels(node))
      );
      globalLeftmostX = Math.min(globalLeftmostX, genLeftmostX);
    }
  });
  
  // Use a fixed X position for all labels (vertically aligned)
  const labelXPosition = globalLeftmostX - 600; // Increased offset to move labels further left and prevent overlap

  // Add root member label above Gen 1 (will be positioned after we know Gen 1 node positions)
  let rootMemberData = null;
  if (treeData && treeData.length > 0) {
    const rootMember = treeData[0];
    const {
      mainMemberName,
      spouseName,
      mainMemberGender,
      spouseNameGender,
    } = getFamilyTreeRootNameParts(rootMember);
    if (mainMemberName || spouseName) {
      rootMemberData = {
        mainMemberName,
        mainMemberGender,
        spouseName,
        spouseNameGender,
      };
    }
  }

  // Add labels for all generations
  const nodeHeight = 150; // Approximate height of family member nodes
  const labelSpacing = 25; // Spacing between labels for generation 2

  // Track grand totals across all generations
  let grandTotalCount = 0;
  let grandMalesCount = 0;
  let grandFemalesCount = 0;
  let lastGenerationY = 0; // Track the Y position of the last generation for positioning grand total
  let gen1LabelY = null; // Track Gen 1 label Y position for root label positioning

  /** Per-generation counts from API `tree` (authoritative for Gen X labels). */
  const apiGenCounts = aggregateGenerationCountsFromApiTree(treeData);

  Object.keys(nodesByGeneration).sort((a, b) => Number(a) - Number(b)).forEach(genNum => {
    const gen = Number(genNum);
    const generationNodes = nodesByGeneration[gen];
    
    if (generationNodes.length > 0) {
      const nodeTopY = generationNodes[0]?.position.y ?? (gen === 1 ? 0 : gen1ToGen2Spacing + (gen - 2) * nodeSpacingY);
      
      let totalCount = 0;
      let malesCount = 0;
      let femalesCount = 0;
      let inLawsCount = 0;

      const fromApi = apiGenCounts[gen];
      if (fromApi) {
        totalCount = fromApi.totalCount;
        malesCount = fromApi.malesCount;
        femalesCount = fromApi.femalesCount;
        inLawsCount = fromApi.inLawsCount;
      } else {
        generationNodes.forEach(node => {
          if (node.type === 'familyMember') {
            const nodeGen = Number(
              node.data?.generation_level ?? node.data?.generation ?? gen
            );
            const hasSpouseOnCard =
              Boolean(node.data?.spouseFullName) ||
              Boolean(node.data?.spouseFirstName) ||
              Boolean(node.data?.spouse_first_name);

            totalCount++;
            const gender = node.data?.gender;
            if (gender !== null && gender !== undefined) {
              if (gender === 1) {
                malesCount++;
              } else if (gender === 2) {
                femalesCount++;
              } else if (gender === 3) {
                /* binary skipped */
              }
            } else if (typeof gender === 'string') {
              const genderLower = gender.toLowerCase();
              if (genderLower === 'male' || genderLower === 'm') {
                malesCount++;
              } else if (genderLower === 'female' || genderLower === 'f') {
                femalesCount++;
              }
            }

            if (nodeGen === 1 && hasSpouseOnCard) {
              totalCount++;
              const spouseG = node.data?.spouseGender ?? node.data?.spouse_gender;
              if (spouseG !== null && spouseG !== undefined) {
                const sn = Number(spouseG);
                if (sn === 1 || spouseG === 1) {
                  malesCount++;
                } else if (sn === 2 || spouseG === 2) {
                  femalesCount++;
                } else if (typeof spouseG === 'string') {
                  const gl = spouseG.toLowerCase();
                  if (gl === 'male' || gl === 'm') malesCount++;
                  else if (gl === 'female' || gl === 'f') femalesCount++;
                }
              }
            } else if (hasSpouseOnCard) {
              inLawsCount++;
            }
          }
        });
      }
      
      // Add to grand totals
      grandTotalCount += totalCount;
      grandMalesCount += malesCount;
      grandFemalesCount += femalesCount;
      
      // Calculate the center Y position of the nodes in this generation
      const nodeCenterY = nodeTopY + (nodeHeight / 2);
      lastGenerationY = nodeCenterY; // Update last generation Y position
      
      if (gen === 1) {
        // Generation 1: "Start/Top of Line" (yellow, same as Gen 2 heads banner) then Gen 1 stats
        const headOfLineLabelHeight = 45;
        const genLabelHeight = 120;
        const gapBetweenLabels = 25;
        const labelGroupHeight = headOfLineLabelHeight + gapBetweenLabels + genLabelHeight;
        const labelGroupTop = nodeCenterY - (labelGroupHeight / 2);
        gen1LabelY = labelGroupTop + headOfLineLabelHeight + gapBetweenLabels;

        const labelNodeStartTop = {
          id: `label-generation-${gen}-start-top`,
          type: 'label',
          position: {
            x: labelXPosition + generationLabelWidth / 2 - gen1StartTopLabelXNudge,
            y: labelGroupTop,
          },
          data: {
            label: 'Start/Top of Line',
            generation: gen,
            hasYellowBackground: true,
          },
          draggable: false,
          selectable: false,
          deletable: false,
          width: 250,
          height: headOfLineLabelHeight,
          style: {
            background: 'transparent',
            border: 'none',
            padding: 0,
          },
          hidden: false,
        };
        nodes.push(labelNodeStartTop);

        const labelNode = {
          id: `label-generation-${gen}`,
          type: 'label',
          position: {
            x: labelXPosition + generationLabelWidth / 2 - gen1StartTopLabelXNudge,
            y: labelGroupTop + headOfLineLabelHeight + gapBetweenLabels,
          },
          data: {
            label: `Gen ${gen}`,
            generation: gen,
            totalCount: totalCount,
            malesCount: malesCount,
            femalesCount: femalesCount,
            inLawsCount: inLawsCount,
          },
          draggable: false,
          selectable: false,
          deletable: false,
          width: 250,
          height: genLabelHeight,
          style: {
            background: 'transparent',
            border: 'none',
            padding: 0,
          },
          hidden: false,
        };
        nodes.push(labelNode);
      } else if (gen === 2) {
        // Generation 2: Two labels - "Head of Family Line/Color" first, then "Gen 2 (18)" with counts
        // Calculate total height of both labels with larger fonts
        const headOfLineLabelHeight = 45; // Height for "Head of Family Line/Color" label
        const genLabelHeight = 120; // Increased to accommodate larger fonts (32px + 28px + 28px for In-Laws with line-height)
        const gapBetweenLabels = 25; // Increased gap between labels for more spacing below yellow label
        const labelGroupHeight = headOfLineLabelHeight + gapBetweenLabels + genLabelHeight;
        const labelGroupTop = nodeCenterY - (labelGroupHeight / 2);
        
        // Label 1: "Head of Family Line/Color" (first label, positioned at top)
        const labelNode1 = {
          id: `label-generation-${gen}-head-of-line`,
          type: 'label',
          position: {
            x: labelXPosition + generationLabelWidth / 2 + gen2HeadsLabelXNudge,
            y: labelGroupTop, // Start at top of label group
          },
          data: {
            label: 'Heads of Family Lines/Colors',
            generation: gen,
            hasYellowBackground: true // Flag to indicate yellow background
          },
          draggable: false,
          selectable: false,
          deletable: false,
          width: 250,
          height: headOfLineLabelHeight,
          style: {
            background: 'transparent',
            border: 'none',
            padding: 0,
          },
          hidden: false,
        };
        nodes.push(labelNode1);
        
        // Label 2: "Gen 2 (Total) (Males Males, Females Females)" (second label, positioned below first)
        const labelNode2 = {
          id: `label-generation-${gen}-top`,
          type: 'label',
          position: {
            x: labelXPosition + generationLabelWidth / 2,
            y: labelGroupTop + headOfLineLabelHeight + gapBetweenLabels
          },
          data: {
            label: `Gen ${gen}`,
            generation: gen,
            totalCount: totalCount,
            malesCount: malesCount,
            femalesCount: femalesCount,
            inLawsCount: inLawsCount
          },
          draggable: false,
          selectable: false,
          deletable: false,
          width: 250,
          height: genLabelHeight,
          style: {
            background: 'transparent',
            border: 'none',
            padding: 0,
          },
          hidden: false,
        };
        nodes.push(labelNode2);
      } else {
        // Generation 3+: Single label "Gen X (Total) (Males Males, Females Females, In-Laws)"
        // Center the label vertically with the node (label height increased to accommodate In-Laws)
        const labelHeight = 120; // Increased to accommodate larger fonts (32px + 28px + 28px for In-Laws with line-height)
        const labelY = nodeCenterY - (labelHeight / 2);
        const labelNode = {
          id: `label-generation-${gen}`,
          type: 'label',
          position: {
            x: labelXPosition + generationLabelWidth / 2,
            y: labelY
          },
          data: {
            label: `Gen ${gen}`,
            generation: gen,
            totalCount: totalCount,
            malesCount: malesCount,
            femalesCount: femalesCount,
            inLawsCount: inLawsCount
          },
          draggable: false,
          selectable: false,
          deletable: false,
          width: 250,
          height: labelHeight,
          style: {
            background: 'transparent',
            border: 'none',
            padding: 0,
          },
          hidden: false,
        };
        nodes.push(labelNode);
      }
    }
  });

  // Add root member label above Gen 1 root head node (above the "HEAD OF THE LINE" badge)
  if (rootMemberData && nodesByGeneration[1] && nodesByGeneration[1].length > 0) {
    // Get the Gen 1 root head node position
    const gen1Node = nodesByGeneration[1][0];
    const gen1NodeY = gen1Node?.position.y || 0;
    const gen1NodeX = gen1Node?.position.x || 0;
    
    // Position root label above the generation badge ("HEAD OF THE LINE")
    // The generation badge is positioned at top: -13px relative to the node
    // Badge height is approximately 25-30px with padding
    // So badge top is at: gen1NodeY - 13px, and badge extends to about gen1NodeY - 13px - 30px
    const badgeTopOffset = 13; // Badge is at -13px from node top
    const badgeHeight = 30; // Approximate badge height
    const badgeTopY = gen1NodeY - badgeTopOffset;
    const labelHeight = 420; // Height of the label node
    const spacingAboveBadge = 10; // Minimal space between root label and top of badge
    // Position label so its bottom edge is just above the badge - move it up
    const rootLabelY = badgeTopY - badgeHeight - spacingAboveBadge - labelHeight;
    
    // position.x is horizontal center (see nodeOrigin [0.5, 0] on Family Tree ReactFlow)
    const nodeCenterX = gen1NodeX;

    const rootLabelNode = {
      id: 'label-root-member',
      type: 'label',
      position: {
        x: nodeCenterX,
        y: rootLabelY
      },
      data: {
        label: 'rootMemberNames', // Special label type
        mainMemberName: rootMemberData.mainMemberName,
        mainMemberGender: rootMemberData.mainMemberGender,
        spouseName: rootMemberData.spouseName,
        spouseNameGender: rootMemberData.spouseNameGender
      },
      draggable: false,
      selectable: false,
      deletable: false,
      width: 2500,
      height: 420,
      zIndex: 1000, // Ensure label is on top
      style: {
        background: 'transparent',
        border: 'none',
        padding: 0,
        zIndex: 1000, // Ensure label is on top
      },
      hidden: false,
    };
    
    // Add root label at the beginning of nodes array to ensure it's processed first
    nodes.unshift(rootLabelNode);
  }

  // Grand Total is now displayed only in the legend, not as a tree label

  // console.log('Generated nodes:', nodes);
  // console.log('Generated edges:', edges);

  // INVERT THE TREE if sortOrder is "youngest"
  // if (sortOrder === "youngest") {
  //   const maxY = Math.max(...nodes.map(node => node.position.y));
    
  //   nodes.forEach(node => {
  //     // Invert Y position: top becomes bottom, bottom becomes top
  //     node.position.y = maxY - node.position.y;
  //   });
    
  //   console.log('Tree inverted for youngest-to-oldest view');
  // }

  return { nodes, edges };
};


/**
 * Auto-layout algorithm for family tree
 * @param {Array} nodes - React Flow nodes
 * @param {Array} edges - React Flow edges
 * @returns {Array} Updated nodes with calculated positions
 */
export const autoLayoutTree = (nodes, edges) => {
  // Implementation can use dagre or elkjs for automatic layout
  // For now, return nodes as-is (manual positioning)
  return nodes;
};

